import { Fragment, type CSSProperties, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useScrollMotion from '../../hooks/useScrollMotion';
import { CONTACT } from '../../config/contact';
import {
  BoltIcon,
  BuildingIcon,
  CheckIcon,
  DropIcon,
  DuvetIcon,
  LayersIcon,
  LeafIcon,
  PhoneIcon,
  SavingsIcon,
  ShieldIcon,
} from '../../components/Icons/Icons';
import m from '../../styles/marketing.module.css';
import styles from './Management.module.css';

/** The flyer's six advantage icons, in the order the copy lists them. */
const ADVANTAGE_ICONS: Array<() => ReactElement> = [
  LeafIcon,
  ShieldIcon,
  LayersIcon,
  DuvetIcon,
  BuildingIcon,
  SavingsIcon,
];

/** Icons for the four engineering points, in copy order: all-metal build,
    higher wash temps, heavy-duty motors, anti-mold. */
const ENG_ICONS: Array<() => ReactElement> = [ShieldIcon, DropIcon, BoltIcon, LeafIcon];

/**
 * The management case's photo band: what residents actually wash, in copy
 * order with the first card (bulky items). Alt keys point at the tenant page's
 * `home` namespace, which already describes these exact shots.
 */
const CASE_PHOTOS = [
  { file: 'bedding_1.jpeg', altKey: 'sizeMatters.pairAlts.0' },
  { file: '24_access.jpeg', altKey: 'wall.images.1' },
  { file: 'looking-washer.jpeg', altKey: 'sizeMatters.imageAlt' },
];

/** The flyer's three-up photo strip, now heading the residents section. */
const RESIDENT_PHOTOS = ['no-mold.jpeg', 'bed-overhead.jpg', 'size-matters.jpg'];

interface CaseItem { num: string; title: string; body: string }
interface NumberedRow { num: string; title: string; body: string }
interface AdvItem { title: string; body: string }
interface EngPoint { lead: string; body: string }
interface ReturnRow {
  scale: string; machines: string; monthly: string;
  annual: string; fiveYear: string;
}

/**
 * The building-management page, in the order the pitch is made to a manager:
 *
 *   1. Hero (light blue) — the problem and the offer.
 *   1b. Good to great (white) — the Good → Great comparison table.
 *   2. The management case (tint) — the problem, a photo band, four reasons.
 *   3. The deal (white) — what we fund and how the partnership works, closed
 *      by the long-term commitment and the hands-off list.
 *   3b. Owner returns (navy) — the headline stat and the returns table.
 *   4. The machine (tint) — the exploded view and the engineering points.
 *   5. What your residents get (white) — photo strip and six advantages.
 *
 * There is no closing contact section; the hero's "Request a site visit"
 * button opens an email, and the site footer carries the contact details.
 *
 * Every section after the hero opens with a kinetic chapter word, as on the
 * tenant page, and goes straight to its content — no eyebrow or kicker
 * repeating it. Grounds alternate so no two neighbours share one.
 */
const Management = () => {
  const { t } = useTranslation('management');
  const { t: tHome } = useTranslation('home');
  const root = useScrollMotion<HTMLDivElement>();

  const list = <T,>(key: string) =>
    t(key, { returnObjects: true }) as unknown as T[];

  const features = list<string>('hero.features');
  const ggRowLabels = list<string>('goodToGreat.rowLabels');
  const ggGood = list<string>('goodToGreat.good.points');
  const ggGreat = list<string>('goodToGreat.great.points');
  const caseItems = list<CaseItem>('case.items');
  const fundRows = list<NumberedRow>('deal.fund.rows');
  const termRows = list<NumberedRow>('deal.terms.rows');
  const handsOff = list<string>('deal.handsOff.checklist');
  const returnCols = list<string>('returns.columns');
  const returnRows = list<ReturnRow>('returns.rows');
  const engPoints = list<EngPoint>('engineering.points');
  const photoAlts = list<string>('residents.photos');
  const advItems = list<AdvItem>('residents.items');

  const hl = { hl: <span /> };

  /**
   * A section's chapter word, as on the tenant page. The scroll layer slides it
   * in from off-side, so it carries no CSS animation of its own — the two would
   * fight over the inline transform. Sides alternate down the page.
   */
  const kinetic = (key: string, dir: 'left' | 'right', light = false) => (
    <div className={light ? `${m.kinetic} ${m.kineticLight}` : m.kinetic} data-scrub={dir}>
      <Trans i18nKey={key} ns="management" components={hl} />
    </div>
  );

  const numberedRows = (rows: NumberedRow[]) =>
    rows.map((row, i) => (
      <div
        key={row.num}
        className={styles.partRow}
        data-reveal
        data-reveal-delay={i * 70}
      >
        <div className={styles.partNum}>{row.num}</div>
        <div>
          <h3>{row.title}</h3>
          <p>{row.body}</p>
        </div>
      </div>
    ));

  return (
    <div ref={root}>
      {/* 1. Hero — headline, lead, the two buttons and the three things a
           manager needs to hear first, beside the machine and its 23kg badge.
           On the tenant page's light blue field, in its button and type
           system, so the two audiences land on the same site. */}
      <header className={styles.hero}>
        <div className={styles.heroInner}>
          <div>
            <div className={`${styles.heroEyebrow} ${styles.rise}`}>{t('hero.eyebrow')}</div>
            <h1 className={`${styles.heroTitle} ${styles.rise}`} data-d="1">
              <Trans i18nKey="hero.title" ns="management" components={hl} />
            </h1>
            <p className={`${styles.heroLead} ${styles.rise}`} data-d="2">
              <Trans i18nKey="hero.lead" ns="management" components={hl} />
            </p>
            <div className={`${styles.heroCta} ${styles.rise}`} data-d="3">
              {/* Straight to email: the page no longer has a contact section
                  to scroll to. */}
              <a href={`mailto:${CONTACT.email}`} className={`${m.btn} ${m.btnMd} ${m.btnPrimary}`}>
                {t('hero.ctaPrimary')}
              </a>
              <a href="#deal" className={`${m.btn} ${m.btnMd} ${m.btnOutline}`}>
                {t('hero.ctaSecondary')}
              </a>
            </div>
            <div className={`${styles.heroFeats} ${styles.rise}`} data-d="4">
              {features.map((feature, i) => (
                <Fragment key={feature}>
                  {i > 0 && <i>●</i>}
                  <span>{feature}</span>
                </Fragment>
              ))}
            </div>
          </div>
          <div className={styles.heroImg}>
            <img src="/images/stackcombo.webp" alt={t('hero.imageAlt')} />
            <div className={styles.heroBadge}>
              <b>{t('hero.badgeValue')}</b>
              <span>{t('hero.badgeLabel')}</span>
            </div>
          </div>
        </div>
      </header>

      {/* 1b. Good to great — the headline, the summary, and the size gap as a
           Good → Great table. White ground, so the tinted management case below
           still reads as its own chapter. */}
      <section className={m.section} id="good-to-great">
        <div className={m.container}>
          {kinetic('goodToGreat.kinetic', 'left')}
          {/* One flex line. "Good" and "Great" take the colours of the table
              columns they head. */}
          <h2 className={styles.ggTitle}>
            <Trans
              i18nKey="goodToGreat.title"
              ns="management"
              components={{
                good: <span className={styles.ggTitleGood} />,
                great: <span className={styles.ggTitleGreat} />,
              }}
            />
          </h2>
          <p className={styles.ggSummary}>
            <Trans i18nKey="goodToGreat.summary" ns="management" components={hl} />
          </p>
          {/* The size gap. Each row reads left to right, label → what most
              condos have → what a STACKCOMBO adds.
              "Good" is still good: it is never crossed out, just set plainly —
              the Great column wins by being the raised green card with checks. */}
          {/* One grid; the rows are `display: contents`, so every cell sits in
              the same three columns and the Great card behind column 3 can
              span the whole table as a single raised panel. Every cell is
              placed explicitly (row from --gg-row) — auto-placement would
              refuse to put a cell on top of the card. */}
          <div className={styles.ggTable} role="table" data-reveal>
            <div
              className={styles.ggGreatCard}
              style={{ gridRow: `1 / span ${ggRowLabels.length + 1}` }}
              aria-hidden="true"
            />
            <div
              className={styles.ggRow}
              role="row"
              style={{ '--gg-row': 1 } as CSSProperties}
            >
              <span className={styles.ggCorner} role="columnheader" />
              {(['good', 'great'] as const).map((side) => (
                <div
                  key={side}
                  role="columnheader"
                  className={`${styles.ggHead} ${side === 'good' ? styles.ggGood : styles.ggGreat}`}
                >
                  <span className={styles.ggTag}>{t(`goodToGreat.${side}.tag`)}</span>
                  <span className={styles.ggColTitle}>{t(`goodToGreat.${side}.title`)}</span>
                </div>
              ))}
            </div>
            {ggRowLabels.map((label, row) => (
              <div
                key={label}
                className={styles.ggRow}
                role="row"
                style={{ '--gg-row': row + 2 } as CSSProperties}
              >
                <div className={styles.ggRowLabel} role="rowheader">{label}</div>
                {/* data-tag feeds the phone layout, where the header row is
                    hidden and each answer carries its own GOOD / GREAT tag. */}
                <div
                  className={`${styles.ggCell} ${styles.ggGood}`}
                  role="cell"
                  data-tag={t('goodToGreat.good.tag')}
                >
                  <span>{ggGood[row]}</span>
                </div>
                <div
                  className={`${styles.ggCell} ${styles.ggGreat}`}
                  role="cell"
                  data-tag={t('goodToGreat.great.tag')}
                >
                  <CheckIcon /><span>{ggGreat[row]}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Commented out on request — the navy "Laundry shapes how tenants
              see the building" band. Copy is still in goodToGreat.impact.
          <div className={styles.ggImpact} data-reveal>
            <h3>{t('goodToGreat.impact.title')}</h3>
            <p>{t('goodToGreat.impact.body')}</p>
          </div>
          */}
        </div>
      </section>

      {/* 2. The management case — the chapter word carries the claim, so the
           section goes straight to the problem, a photo band of what residents
           actually wash, and the four cards. */}
      <section className={`${m.section} ${m.tint}`} id="case">
        <div className={m.container}>
          {kinetic('case.kinetic', 'right')}
          {/* The problem the four cards answer. Body copy, so it keeps a
              reading measure. */}
          <p className={styles.caseSub}>{t('case.sub')}</p>
          {/* The page's first photographs after the hero: the bedding, the
              duvet going into a commercial drum, a resident at the machine.
              Alt text is the tenant page's, which shows the same shots. */}
          <div className={styles.caseBand} data-reveal>
            {CASE_PHOTOS.map(({ file, altKey }) => (
              <img key={file} src={`/images/${file}`} alt={tHome(altKey)} />
            ))}
          </div>
          <div className={styles.caseGrid}>
            {caseItems.map((item, i) => (
              <div
                key={item.num}
                className={styles.caseCard}
                data-reveal
                data-reveal-delay={i * 90}
              >
                <div className={styles.caseNum}>{item.num}</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. The deal — what we fund beside how the partnership works, then the
           navy close: the long-term commitment and the hands-off list.

           The copy commits to structure only. Percentages, term lengths and
           the utilities accounting are "agreed on the site visit"; the exact
           wording of each terms row still needs client sign-off against the
           actual contract. No figures here without it. */}
      <section className={m.section} id="deal">
        <div className={m.container}>
          {kinetic('deal.kinetic', 'left')}
          {/* The head states what we supply; the two columns below detail it. */}
          <h2 className={m.columnHead}>{t('deal.head')}</h2>
          <div className={styles.dealGrid}>
            <div>
              <h3 className={styles.dealColHead}>{t('deal.fund.title')}</h3>
              <div className={styles.partList}>{numberedRows(fundRows)}</div>
            </div>
            <div>
              <h3 className={styles.dealColHead}>{t('deal.terms.title')}</h3>
              <div className={styles.partList}>{numberedRows(termRows)}</div>
            </div>
          </div>
          {/* A light card, not a navy box — the returns section right below is
              the page's navy moment. */}
          <div className={styles.dealClose} data-reveal>
            <div>
              <h3 className={styles.dealCloseTitle}>{t('deal.commitment.title')}</h3>
              <p className={styles.dealCloseBody}>{t('deal.commitment.body')}</p>
            </div>
            <div className={styles.handsOff}>
              <h4>{t('deal.handsOff.title')}</h4>
              <p>{t('deal.handsOff.body')}</p>
              <ul>
                {handsOff.map((item) => (
                  <li key={item}><CheckIcon />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3b. Owner returns — the deal above commits to the structure; this is
           what that structure pays a building, at one machine and at a
           thousand. A table rather than separate figures: the argument is the
           relationship between the columns, which only reads when they sit
           next to each other.

           Five columns do not fit a phone, so the table scrolls inside its own
           box rather than shrinking the type or stacking into unreadable
           cards.

           Navy, so the figures read as the page's payoff and the section does
           not run on from the white deal above it. The headline stat is the
           one-machine five-year figure, read from the table's own first row so
           the two can never disagree. */}
      <section className={`${m.section} ${m.navy}`} id="returns">
        <div className={m.container}>
          {kinetic('returns.kinetic', 'right', true)}
          <div className={styles.returnsHead}>
            <div>
              <h2 className={styles.returnsTitle}>{t('returns.head')}</h2>
              <p className={styles.returnsLead}>{t('returns.lead')}</p>
            </div>
            {returnRows[0] && (
              <div className={styles.returnsStat} data-reveal>
                <div className={styles.returnsStatValue}>{returnRows[0].fiveYear}</div>
                <div className={styles.returnsStatLabel}>{t('returns.statLabel')}</div>
              </div>
            )}
          </div>
          <div className={styles.returnsWrap} data-reveal>
            <table className={styles.returnsTable}>
              <thead>
                <tr>
                  {returnCols.map((col) => (
                    <th key={col} scope="col">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {returnRows.map((row) => (
                  <tr key={row.scale}>
                    <th scope="row">{row.scale}</th>
                    <td>{row.machines}</td>
                    <td>{row.monthly}</td>
                    <td>{row.annual}</td>
                    <td>{row.fiveYear}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.returnsNote}>{t('returns.note')}</p>
        </div>
      </section>

      {/* 4. The machine — a split: the exploded view large on the left, the
           argument on the right (eyebrow, title, the four points as a 2×2 of
           icon cards), closed by the app line as a navy strip under both.
           The picture and the points sit side by side so each point reads
           against the part of the machine it describes. Tinted, not navy,
           because the returns section directly above is the navy one. */}
      <section className={`${m.section} ${m.tint}`} id="machine">
        <div className={m.container}>
          {kinetic('engineering.kinetic', 'left')}
          <div className={styles.engSplit}>
            <div className={styles.engMedia} data-reveal>
              <img src="/images/exploded.jpg" alt={t('engineering.imageAlt')} />
            </div>
            <div>
              <div className={m.eyebrow}>{t('engineering.eyebrow')}</div>
              <h2 className={styles.engTitle}>{t('engineering.title')}</h2>
              <div className={styles.engPoints}>
                {engPoints.map((point, i) => {
                  const Icon = ENG_ICONS[i] ?? CheckIcon;
                  return (
                    <div
                      key={point.lead}
                      className={styles.engPoint}
                      data-reveal
                      data-reveal-delay={i * 80}
                    >
                      <div className={styles.engPointIcon}><Icon /></div>
                      <div className={styles.engPointLead}>{point.lead}</div>
                      <p>{point.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={styles.engApp} data-reveal>
            <div className={styles.appIcon}><PhoneIcon /></div>
            <p>
              <Trans i18nKey="engineering.app" ns="management" components={{ b: <b /> }} />
            </p>
          </div>
        </div>
      </section>

      {/* 5. What your residents get — the flyer's photo strip and its six
           advantages, each making a different point: footprint, hygiene,
           capacity, what fits, access, cost. */}
      <section className={m.section} id="residents">
        <div className={m.container}>
          {kinetic('residents.kinetic', 'right')}
          <p className={`${m.lead} ${styles.residentsLead}`}>{t('residents.lead')}</p>
          <div className={styles.photoStrip} data-reveal>
            {RESIDENT_PHOTOS.map((file, i) => (
              <img key={file} src={`/images/${file}`} alt={photoAlts[i]} />
            ))}
          </div>
          <div className={styles.advList}>
            {advItems.map((item, i) => {
              const Icon = ADVANTAGE_ICONS[i] ?? LayersIcon;
              return (
                <div
                  key={item.title}
                  className={styles.advItem}
                  data-reveal
                  data-reveal-delay={i * 70}
                >
                  <div className={m.iconChip}><Icon /></div>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Management;
